import { useEffect, useRef, useState } from 'react';

// Manual activation: arrow keys move focus; native Enter/Space clicks select.
// Labels arrive already translated: call sites keep different i18n
// key conventions (`platformConsole.tab*`, a `TAB_I18N` lookup with an English
// fallback), so calling `t()` in here would leak those namespaces into a
// component that should not know them.

export function TabStrip<Id extends string>({
  tabs,
  value,
  onChange,
  hidden,
  ariaLabel,
  level = 'console'
}: {
  tabs: ReadonlyArray<{ id: Id; label: string }>;
  value: Id;
  onChange: (id: Id) => void;
  /** Hides the strip when a caller narrows the visible set to a single tab.
   *  Works only because `.tab-strip[hidden]` guards against the author-level
   *  `display: flex` beating the UA `[hidden]` rule — see tables.css. */
  hidden?: boolean;
  ariaLabel?: string;
  /** `'console'` = the page's primary strip (underlined, one per screen).
   *  `'nested'` = sub-tabs INSIDE a console tab (pills). Two identical strips
   *  stacked read as one flat row of unrelated tabs — worse than the vertical
   *  stack they replaced — so anything under another strip must say so. */
  level?: 'console' | 'nested';
}) {
  const strip = useRef<HTMLDivElement>(null);
  const [focusedId, setFocusedId] = useState(value);
  const entryId = tabs.some((item) => item.id === focusedId)
    ? focusedId
    : tabs.find((item) => item.id === value)?.id ?? tabs[0]?.id;

  useEffect(() => {
    setFocusedId(value);
    // External selection (e.g. the compact selector) updates the entry point.
    // Only move DOM focus if it was already inside this visible strip.
    const root = strip.current;
    if (!hidden && root?.contains(document.activeElement) && root.getClientRects().length) {
      root.querySelector<HTMLButtonElement>('[aria-selected="true"]')?.focus();
    }
  }, [value, hidden]);

  return (
    <div
      ref={strip}
      className={level === 'nested' ? 'tab-strip nested' : 'tab-strip'}
      role="tablist"
      aria-label={ariaLabel}
      hidden={hidden}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setFocusedId(value);
      }}
    >
      {tabs.map((item) => (
        <button
          key={item.id}
          type="button"
          role="tab"
          aria-selected={item.id === value}
          tabIndex={!hidden && item.id === entryId ? 0 : -1}
          className={item.id === value ? 'active' : undefined}
          onFocus={() => setFocusedId(item.id)}
          onKeyDown={(event) => {
            const buttons = Array.from(strip.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]') ?? []);
            const index = buttons.indexOf(event.currentTarget);
            let next: number;
            switch (event.key) {
              case 'ArrowRight': next = (index + 1) % buttons.length; break;
              case 'ArrowLeft': next = (index - 1 + buttons.length) % buttons.length; break;
              case 'Home': next = 0; break;
              case 'End': next = buttons.length - 1; break;
              default: return;
            }
            event.preventDefault();
            buttons[next]?.focus();
          }}
          onClick={() => onChange(item.id)}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}
